import { registerPlugin, Capacitor } from '@capacitor/core'

/**
 * Мост к нативному Yandex LoginSDK (iOS/Android) через Capacitor.
 *
 * Веб-поток Яндекс ID у нас серверный: `/auth/yandex/initiate` → открыть `yandexAuthUrl`
 * во внешнем браузере → поллинг `/auth/yandex/status/:sessionId`. В приложении это
 * означает уход в системный браузер и возврат «наугад»: пользователь видит чужую вкладку,
 * а мы ждём ответа поллингом. Нативный LoginSDK делает вход внутри приложения
 * (приложение Яндекса / Safari-контроллер) и сразу отдаёт OAuth-токен.
 *
 * Нативная сторона инициализируется Client ID приложения Яндекс OAuth (задаётся на этапе
 * сборки — Info.plist `YandexClientID` на iOS, `strings.xml`/manifestPlaceholders на
 * Android; это НЕ приходит с бэка, потому что от него зависят URL-схемы приложения).
 * Полученный токен уходит на бэк `POST /auth/yandex/exchange`, где независимо проверяется
 * у Яндекса — токен чужого приложения там отклоняется.
 *
 * @see https://yandex.ru/dev/id/doc/ru/mobileauthsdk/ios/sdk-ios-main
 */
export interface YandexIdNativeAuthResult {
  /** OAuth-токен Яндекс ID (`LoginResult.token` / `YandexAuthToken.value`). */
  oauthToken: string
  /** JSON Web Token от SDK, если платформа его вернула (в проверке не участвует). */
  jwt?: string
  /** Время жизни токена в секундах (отдаёт только Android SDK). */
  expiresIn?: number
}

export interface YandexIdAuthPlugin {
  /** Запускает нативный экран авторизации Яндекс ID и возвращает OAuth-токен. */
  authorize(): Promise<YandexIdNativeAuthResult>
  /**
   * Разлогин в нативном SDK. Важен на iOS: SDK хранит токен на устройстве и без
   * logout повторный `authorize()` молча вернёт токен ПРЕДЫДУЩЕГО аккаунта.
   */
  logout(): Promise<void>
}

/**
 * Прокси-плагин. Если нативная реализация не зарегистрирована (веб или сборка без
 * модуля LoginSDK) — вызовы отклоняются, и вызывающий код откатывается на веб-поток.
 */
export const YandexIdAuth = registerPlugin<YandexIdAuthPlugin>('YandexIdAuth')

/** Доступен ли нативный LoginSDK Яндекс ID на текущей платформе. */
export const isYandexIdNativeAvailable = (): boolean => {
  try {
    return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('YandexIdAuth')
  } catch {
    return false
  }
}
